import React, { CSSProperties, FC, ReactNode } from 'react'
import { Button, ButtonProps, Menu, Dropdown, DropdownProps } from 'src/antd'
import styled from 'styled-components'

import {
  useIsBelowDesktop,
  useIsCoarsePointerBelowDesktop,
} from 'src/hooks/useViewport'
import { MoreOutlined } from 'src/icons/antd-compat'
import {
  TOUCH_TARGET_MIN,
  coarsePointerBelowDesktop,
} from 'src/styles/breakpoints'

/**
 * PRO-929 — the row's menu trigger, sized for a finger where there is one.
 *
 * The trigger was roughly a 16px glyph sitting next to the row's other
 * controls, with a destructive action among them. Inside the narrow band, on
 * a touch screen it gets the platform touch-target floor; under a mouse, or
 * at any width from 992px up, nothing here applies — so no table's row height
 * changes for a desktop user, touchscreen laptop included.
 *
 * ON THE BUTTON, not a wrapper around it. A wrapper would be a new DOM node
 * at EVERY width, including the one that must render exactly as it does
 * today, for all ~76 call sites — and growing the button grows the row just
 * the same. `display` has to be restated because antd 5.15's `.ant-btn` is
 * `inline-block`, not `inline-flex`: `text-align: center` would centre the
 * glyph across the 40px but leave it at the top of it. The doubled `&&` is
 * the usual guard against antd's own unlayered `.ant-btn` rule, which has
 * equal specificity.
 */
const StyledButton = styled(Button)`
  padding: 0 8px;
  font-size: 14px;
  line-height: 16px;

  ${coarsePointerBelowDesktop} {
    && {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: ${TOUCH_TARGET_MIN}px;
      min-height: ${TOUCH_TARGET_MIN}px;
    }
  }
`

type TActionButtonActionArgsProps = {
  danger?: boolean
  disabled?: boolean
  cond?: (d?: $TSFixMe) => boolean
}

export type MenuAction = [
  label: ReactNode,
  action: (d?: $TSFixMe) => void,
  args?: TActionButtonActionArgsProps
]

export type MenuActions =
  | MenuAction
  | [
      label: ReactNode,
      childs: MenuAction[],
      args?: TActionButtonActionArgsProps
    ]

const isAction = (a: MenuActions): a is MenuAction => {
  return typeof a[1] === 'function'
}

export type TActionButtonsType = {
  actions: MenuActions[]
  data?: $TSFixMe
  dropdownOnly?: boolean
  dropdownProps?: DropdownProps
  size?: ButtonProps['size']
  style?: CSSProperties
  className?: string
}

export const ActionButtons: FC<TActionButtonsType> = ({
  actions,
  data,
  size = 'middle',
  dropdownOnly = false,
  dropdownProps,
  className,
  style,
}) => {
  // The seam Menu no longer inherits antd's internal dropdown override, which
  // used to close the dropdown on every item click — so drive it here.
  const [open, setOpen] = React.useState(false)

  /**
   * PRO-929 — row actions on a touch screen.
   *
   * This menu opened on HOVER, which a phone has no way to express: a tap is
   * delivered as a synthesised hover followed by a click, so the panel opened
   * and closed again under the finger. The destructive row actions live in
   * here ("Avslutt avtale" among them), so on a phone they were effectively
   * unreachable.
   *
   * The test is the POINTER, BOUNDED BY THE BAND — a 900px desktop window
   * hovers fine, and a 1920px touchscreen laptop cannot hover but must keep
   * the rendering it has today. So a mouse user at any width, and every user
   * at 992px and up, keeps the hover behaviour exactly as it is, which is the
   * desktop-unchanged claim for this file.
   */
  const isTouch = useIsCoarsePointerBelowDesktop()

  /**
   * Below the desktop floor the leading action stops being its own button and
   * joins the menu, so the row itself is the target rather than a ~16px
   * control beside a destructive one. `dropdownOnly` is the call site's
   * existing opt-in; this adds the narrow-viewport case to it.
   */
  const belowDesktopFloor = useIsBelowDesktop()
  const collapseToMenu = dropdownOnly || belowDesktopFloor

  const [firstAction, ...actionsArray] = actions

  const restArray = collapseToMenu ? actions : actionsArray

  const filteredArray = restArray.filter((a) =>
    !a[2]?.cond ? true : a[2]?.cond?.(data)
  )

  return (
    <div
      style={{ display: 'flex', flexDirection: 'row', gap: 4, ...style }}
      className={className}
    >
      {!collapseToMenu && firstAction && (
        <StyledButton
          danger={firstAction?.[2]?.danger}
          onClick={() => (firstAction as MenuAction)?.[1](data)}
          size={size}
          disabled={firstAction?.[2]?.disabled}
        >
          {firstAction?.[0]}
        </StyledButton>
      )}
      {!!filteredArray.length && (
        <Dropdown
          {...dropdownProps}
          trigger={isTouch ? ['click'] : ['hover']}
          getPopupContainer={() => document.body}
          open={dropdownProps?.open ?? open}
          onOpenChange={(nextOpen, info) => {
            setOpen(nextOpen)
            dropdownProps?.onOpenChange?.(nextOpen, info)
          }}
          dropdownRender={() => (
            <Menu
              onClick={() => setOpen(false)}
              items={filteredArray?.map((action, index) => {
                if (isAction(action)) {
                  return {
                    key: index,
                    onClick: () => action?.[1](data),
                    danger: action?.[2]?.danger,
                    disabled: action?.[2]?.disabled,
                    label: (
                      <div
                        style={{
                          minWidth: '100px',
                          width: 'auto',
                        }}
                      >
                        {action?.[0]}
                      </div>
                    ),
                  }
                }
                return {
                  type: 'group',
                  label: action[0],
                  children: action[1].map((act, subIndex) => ({
                    key: `${index}_${subIndex}`,
                    onClick: () => act?.[1](data),
                    danger: act?.[2]?.danger,
                    disabled: act?.[2]?.disabled,
                    label: (
                      <div
                        style={{
                          minWidth: '100px',
                          width: 'auto',
                        }}
                      >
                        {act?.[0]}
                      </div>
                    ),
                  })),
                }
              })}
            />
          )}
        >
          {/* NO accessible name on this icon-only trigger. That gap is
              PRE-EXISTING and deliberately left alone here: naming it is a
              change an assistive-technology user would notice at every
              width, and ">= 992px behaves exactly as today" is this work's
              hard constraint. Deferred to a separate accessibility pass —
              do not add one back by reflex as part of a responsive change. */}
          <StyledButton size={size} type="text">
            <MoreOutlined />
          </StyledButton>
        </Dropdown>
      )}
    </div>
  )
}
